/** @file Moves a slot to another bone; a slot holding a mounted item must stay on its socket bone. */
import { z } from 'zod';
import { throwError } from '@pose2d/format/throwError';
import type { Doc, Id } from '@pose2d/format/types';
import { defineCommand } from '../defineCommand';

/** MOUNT_INVALID at `bone` when an attachment this slot shows is mounted on a socket whose bone is not `bone`. */
function assertMountsStay(doc: Doc, slotId: Id, bone: Id): void {
  for (const skin of Object.values(doc.skins)) {
    for (const entry of skin.entries) {
      const att = entry.slot === slotId ? doc.attachments[entry.attachment] : undefined;
      const socket = att?.mount ? doc.sockets[att.mount.socket] : undefined;
      if (att && socket && socket.bone !== bone) {
        throwError('MOUNT_INVALID', { attachment: att.name, reason: "the attachment's slot must sit on the socket bone" }, { path: 'bone', hint: `unequip ${att.name} from socket ${socket.name} first` });
      }
    }
  }
}

/** slot.setBone: a no-op when the slot is already on that bone. */
export const command = defineCommand({
  name: 'slot.setBone',
  description: 'Move a slot to another bone (name or id); refused while the slot shows an attachment mounted on a socket (unequip it first).',
  input: z.object({ target: z.string().min(1), bone: z.string().min(1) }).strict(),
  apply(draft, input, ctx) {
    const slotId = ctx.resolve('slot', input.target);
    const bone = ctx.resolve('bone', input.bone);
    const slot = draft.slots[slotId];
    if (!slot || slot.bone === bone) return;
    assertMountsStay(draft, slotId, bone);
    slot.bone = bone;
    draft.meta.updatedAt = ctx.now;
  },
});
